import { useEffect, useState } from "react";
import api, { apiError, fmtUSD, fmtQty } from "@/lib/api";
import { Panel, Metric, Table, Empty, StatusBadge, Money } from "@/components/terminal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { Target } from "lucide-react";

const SCORE_FIELDS = [
  ["asymmetric_upside", "Asymmetric Upside"], ["catalyst_strength", "Catalyst Strength"],
  ["catalyst_timing", "Catalyst Timing"], ["dev_activity", "Dev Activity"],
  ["narrative_momentum", "Narrative / Momentum"], ["exit_liquidity", "Exit Liquidity"],
  ["tokenomics_risk", "Tokenomics / Dilution Risk"], ["unlock_risk", "Unlock Risk"],
  ["concentration_risk", "Concentration Risk"], ["downside_risk", "Downside Risk"],
];

export default function Wildcard() {
  const [d, setD] = useState(null);
  const [rules, setRules] = useState(null);
  const [form, setForm] = useState({ asset: "CRO", amount: "", thesis: "", catalyst: "", expected_horizon: "", risk_factors: "", liquidity_assessment: "", exit_conditions: "" });
  const [scores, setScores] = useState(Object.fromEntries(SCORE_FIELDS.map(([k]) => [k, 5])));
  const [fundAmount, setFundAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [rejection, setRejection] = useState(null);

  const load = () => {
    api.get("/wildcard").then((r) => setD(r.data)).catch(() => {});
    api.get("/risk/rules").then((r) => setRules(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const fund = async (e) => {
    e.preventDefault();
    try {
      await api.post("/wildcard/fund", { amount: parseFloat(fundAmount), source_bucket: "experimental" });
      toast.success("Wildcard funded from experimental capital");
      setFundAmount(""); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const propose = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/wildcard/propose", { ...form, amount: parseFloat(form.amount), scores });
      const p = r.data;
      if (p.status === "REJECTED") {
        setRejection(p.violations || p.risk_result?.violations || []);
        toast.error(`Risk Governor rejected: ${(p.violations || [])[0]?.detail || "rule violation"}`);
      }
      else if (p.status === "ACTIVE") { setRejection(null); toast.success(`Wildcard position ACTIVE: ${fmtQty(p.quantity)} ${p.asset}`); }
      else { setRejection(null); toast.success(`Position ${p.status}`); }
      setForm({ ...form, amount: "", thesis: "" });
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const act = async (pid, action, label) => {
    try {
      const r = await api.post(`/wildcard/positions/${pid}/${action}`);
      if (action === "harvest") toast.success(`Harvested: ${fmtUSD(r.data.net_swept)} swept to ${r.data.swept_to?.replace("_", " ")} (${fmtUSD(r.data.tax_reserved)} to tax reserve)`);
      else toast.success(label);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const saveRules = async () => {
    try {
      await api.put("/wildcard/config", { rules: {
        wildcard_target_pct: rules.wildcard_target_pct, wildcard_max_pct_of_treasury: rules.wildcard_max_pct_of_treasury,
        wildcard_max_position_pct: rules.wildcard_max_position_pct, wildcard_max_daily_deployment: rules.wildcard_max_daily_deployment,
        wildcard_max_weekly_deployment: rules.wildcard_max_weekly_deployment, wildcard_max_positions: rules.wildcard_max_positions,
        wildcard_min_liquidity_usd: rules.wildcard_min_liquidity_usd, wildcard_max_spread_pct: rules.wildcard_max_spread_pct,
      }});
      toast.success("Wildcard policy updated");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  if (!d || !rules) return <div className="text-slate-500 font-mono text-sm">Loading…</div>;

  return (
    <div className="space-y-5" data-testid="wildcard-page">
      <div className="flex flex-wrap items-center gap-3">
        <Target className="w-5 h-5 gold-text" />
        <h2 className="font-display text-xl font-bold">Wildcard — Asymmetric Opportunity Capital</h2>
        <span className="badge bg-amber-500/10 text-amber-400 border-amber-700/60" data-testid="wildcard-risk-badge">High Risk — Controlled Exposure</span>
      </div>

      <div className="panel-elevated p-5 border-l-2 border-l-[#D4AF37]" data-testid="wildcard-hero">
        <div className="metric-label">Core Capital Created From Wildcard Profits</div>
        <div className="font-mono text-4xl font-extrabold tabular-nums gold-text mt-1" data-testid="wildcard-core-created">{fmtUSD(d.core_capital_created)}</div>
        <p className="text-[11px] text-slate-500 mt-2">One-way valve: Wildcard Winner → Tax Assessment → Tax Reserve → Core Treasury. Losses never pull from protected capital.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3">
        <Metric label="Wildcard NAV" value={fmtUSD(d.wildcard_nav)} testId="wildcard-nav" />
        <Metric label="% of Total Treasury" value={`${d.pct_of_treasury}%`} sub={`max ${rules.wildcard_max_pct_of_treasury}%`} testId="wildcard-pct" />
        <Metric label="Available Capital" value={fmtUSD(d.available_capital)} testId="wildcard-available" />
        <Metric label="Capital Deployed" value={fmtUSD(d.capital_deployed)} testId="wildcard-deployed" />
        <Metric label="Unrealized P/L" value={fmtUSD(d.unrealized_pl)} tone={d.unrealized_pl >= 0 ? "pos" : "neg"} testId="wildcard-unrealized" />
        <Metric label="Realized P/L" value={fmtUSD(d.realized_pl)} tone={d.realized_pl >= 0 ? "pos" : "neg"} testId="wildcard-realized" />
        <Metric label="Gains Swept To Core" value={fmtUSD(d.gains_swept_to_core)} tone="gold" testId="wildcard-swept" />
        <Metric label="Est. Tax Liability" value={fmtUSD(d.estimated_tax_liability)} testId="wildcard-tax" />
        <Metric label="Active Positions" value={String(d.active_positions)} sub={`max ${rules.wildcard_max_positions}`} testId="wildcard-active-count" />
        <Metric label="Best Performer" value={d.best_performer ? `${d.best_performer.asset} +${d.best_performer.gain_pct}%` : "—"} testId="wildcard-best" />
        <Metric label="Worst Performer" value={d.worst_performer ? `${d.worst_performer.asset} ${d.worst_performer.gain_pct}%` : "—"} testId="wildcard-worst" />
        <Metric label="Risk Level" value={d.risk_level} tone={d.risk_level === "LOW" ? "pos" : d.risk_level === "MODERATE" ? "gold" : "neg"} testId="wildcard-risk-level" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Panel title="Fund Wildcard" testId="wildcard-fund-panel">
          <form onSubmit={fund} className="space-y-3">
            <div className="metric-label">Amount (USD) — from Experimental bucket only</div>
            <Input data-testid="wildcard-fund-amount" type="number" min="0.01" step="0.01" required value={fundAmount}
              onChange={(e) => setFundAmount(e.target.value)} className="bg-[#121316] border-[#2D323E] font-mono" />
            <Button data-testid="wildcard-fund-button" type="submit" className="w-full bg-[#D4AF37] hover:bg-[#C5A059] text-[#0B0C0E] font-bold">ALLOCATE TO WILDCARD</Button>
            <p className="text-[10px] text-slate-600">Never from Tax Reserve, Operating Cash, Opportunity Reserve, Income/Yield, or Core. No borrowing. No automatic transfers.</p>
          </form>
          <div className="mt-4 pt-3 border-t border-[#222630] space-y-2">
            <div className="metric-label">Policy Limits</div>
            {[["wildcard_target_pct", "Target % of NAV"], ["wildcard_max_pct_of_treasury", "Max % of Treasury"], ["wildcard_max_position_pct", "Max position % of NAV"], ["wildcard_max_daily_deployment", "Max daily deployment $"], ["wildcard_max_weekly_deployment", "Max weekly deployment $"], ["wildcard_max_positions", "Max positions"]].map(([k, label]) => (
              <div key={k} className="flex items-center justify-between gap-2">
                <span className="text-[11px] text-slate-500">{label}</span>
                <Input type="number" step="any" data-testid={`wildcard-rule-${k}`} value={rules[k]}
                  onChange={(e) => setRules({ ...rules, [k]: parseFloat(e.target.value) || 0 })}
                  className="w-24 bg-[#121316] border-[#2D323E] font-mono h-7 text-xs text-right" />
              </div>
            ))}
            <Button data-testid="wildcard-rules-save" onClick={saveRules} variant="outline" size="sm" className="w-full border-[#D4AF37]/50 text-[#D4AF37] mt-1">SAVE POLICY</Button>
          </div>
        </Panel>

        <Panel title="Propose Wildcard Position" testId="wildcard-propose-panel" className="lg:col-span-2">
          <form onSubmit={propose} className="space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div><div className="metric-label mb-1">Asset</div>
                <Input data-testid="wildcard-asset-input" value={form.asset} onChange={(e) => setForm({ ...form, asset: e.target.value.toUpperCase() })} className="bg-[#121316] border-[#2D323E] font-mono" /></div>
              <div><div className="metric-label mb-1">Amount (USD)</div>
                <Input data-testid="wildcard-amount-input" type="number" min="0.01" step="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="bg-[#121316] border-[#2D323E] font-mono" /></div>
              <div><div className="metric-label mb-1">Catalyst</div>
                <Input data-testid="wildcard-catalyst-input" value={form.catalyst} onChange={(e) => setForm({ ...form, catalyst: e.target.value })} className="bg-[#121316] border-[#2D323E]" /></div>
              <div><div className="metric-label mb-1">Time Horizon</div>
                <Input data-testid="wildcard-horizon-input" value={form.expected_horizon} onChange={(e) => setForm({ ...form, expected_horizon: e.target.value })} className="bg-[#121316] border-[#2D323E]" placeholder="e.g. 6-12 months" /></div>
            </div>
            <div><div className="metric-label mb-1">Thesis (required)</div>
              <Textarea data-testid="wildcard-thesis-input" required value={form.thesis} onChange={(e) => setForm({ ...form, thesis: e.target.value })} className="bg-[#121316] border-[#2D323E] min-h-[60px]" placeholder="Why is this asymmetric? What is the upside case?" /></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div><div className="metric-label mb-1">Risk Factors</div>
                <Input data-testid="wildcard-risks-input" value={form.risk_factors} onChange={(e) => setForm({ ...form, risk_factors: e.target.value })} className="bg-[#121316] border-[#2D323E]" /></div>
              <div><div className="metric-label mb-1">Liquidity Assessment</div>
                <Input data-testid="wildcard-liquidity-input" value={form.liquidity_assessment} onChange={(e) => setForm({ ...form, liquidity_assessment: e.target.value })} className="bg-[#121316] border-[#2D323E]" /></div>
              <div><div className="metric-label mb-1">Exit Conditions</div>
                <Input data-testid="wildcard-exit-input" value={form.exit_conditions} onChange={(e) => setForm({ ...form, exit_conditions: e.target.value })} className="bg-[#121316] border-[#2D323E]" placeholder="e.g. thesis invalidated, -40%, +200%" /></div>
            </div>
            <div>
              <div className="metric-label mb-2">Research Inputs (0–10)</div>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                {SCORE_FIELDS.map(([k, label]) => (
                  <div key={k}>
                    <div className="text-[10px] text-slate-500 mb-0.5">{label}</div>
                    <Input type="number" min="0" max="10" step="1" data-testid={`wildcard-score-${k}`} value={scores[k]}
                      onChange={(e) => setScores({ ...scores, [k]: Math.max(0, Math.min(10, parseInt(e.target.value) || 0)) })}
                      className="bg-[#121316] border-[#2D323E] font-mono h-7 text-xs" />
                  </div>
                ))}
              </div>
            </div>
            <Button data-testid="wildcard-propose-button" type="submit" disabled={busy} className="bg-[#D4AF37] hover:bg-[#C5A059] text-[#0B0C0E] font-bold">
              {busy ? "EVALUATING…" : "SCORE & PROPOSE (Risk Governor gated)"}
            </Button>
            {rejection && (
              <div className="border border-red-800/60 bg-red-950/30 rounded-sm px-3 py-2 space-y-1" data-testid="wildcard-rejection-box">
                <div className="text-red-400 text-xs font-bold">RISK GOVERNOR: REJECTED</div>
                {rejection.map((v, i) => <div key={i} className="text-red-300 text-[11px]">✕ {v.rule}: {v.detail}</div>)}
              </div>
            )}
            <p className="text-[10px] text-slate-600">A high Wildcard Score never bypasses the Risk Governor. No automatic averaging down. No leverage. No derivatives.</p>
          </form>
        </Panel>
      </div>

      <Panel title="Wildcard Positions" testId="wildcard-positions-panel">
        {d.positions.length === 0 ? <Empty>No Wildcard positions. Signals generate research and proposals only.</Empty> : (
          <Table testId="wildcard-positions-table" head={[{ label: "Asset" }, { label: "Status" }, { label: "Score", num: true }, { label: "Invested", num: true }, { label: "% NAV", num: true }, { label: "Entry", num: true }, { label: "Current", num: true }, { label: "Gain", num: true }, { label: "Unrealized", num: true }, { label: "Realized", num: true }, { label: "Tax", num: true }, { label: "Thesis" }, { label: "Actions" }]}>
            {d.positions.map((p) => (
              <tr key={p.id} className="hover:bg-[#222630]" data-testid={`wildcard-position-${p.id.slice(0, 8)}`}>
                <td className="td-cell font-semibold text-slate-100">{p.asset}</td>
                <td className="td-cell"><StatusBadge status={p.status} testId={`wildcard-status-${p.id.slice(0, 8)}`} /></td>
                <td className="td-num gold-text">{p.wildcard_score}</td>
                <td className="td-num">{fmtUSD(p.amount_deployed || p.amount_requested)}</td>
                <td className="td-num">{p.pct_of_treasury_at_entry}%</td>
                <td className="td-num">{p.entry_price ? fmtUSD(p.entry_price) : "—"}</td>
                <td className="td-num">{p.current_price ? fmtUSD(p.current_price) : "—"}</td>
                <td className={`td-num ${p.gain_pct > 0 ? "text-emerald-400" : p.gain_pct < 0 ? "text-red-400" : ""}`}>{p.gain_pct != null ? `${p.gain_pct > 0 ? "+" : ""}${p.gain_pct}%` : "—"}</td>
                <td className="td-num"><Money value={p.unrealized_pl} tone={p.unrealized_pl > 0 ? "pos" : p.unrealized_pl < 0 ? "neg" : undefined} /></td>
                <td className="td-num"><Money value={p.realized_pl} tone={p.realized_pl > 0 ? "pos" : p.realized_pl < 0 ? "neg" : undefined} /></td>
                <td className="td-num text-amber-400">{fmtUSD(p.tax_liability)}</td>
                <td className="td-cell text-slate-500 max-w-[220px]">
                  <div className="truncate" title={p.thesis}>{p.thesis}</div>
                  {p.intel_warnings?.length > 0 && (
                    <div className="text-[10px] text-amber-400 mt-0.5" data-testid={`wildcard-warnings-${p.id.slice(0, 8)}`}>
                      Intel: {p.intel_warnings.length} warning(s) — {p.intel_warnings[0].event_type.replace(/_/g, " ")}
                    </div>
                  )}
                </td>
                <td className="td-cell">
                  <div className="flex gap-1.5">
                    {p.status === "APPROVED" && !p.human_approved && <Button size="sm" variant="outline" data-testid={`wildcard-approve-${p.id.slice(0, 8)}`} onClick={() => act(p.id, "approve", "Approved")} className="h-7 text-[11px] border-emerald-800 text-emerald-400">Approve</Button>}
                    {p.status === "APPROVED" && <Button size="sm" variant="outline" data-testid={`wildcard-execute-${p.id.slice(0, 8)}`} onClick={() => act(p.id, "execute", "Executed")} className="h-7 text-[11px] border-[#D4AF37]/50 text-[#D4AF37]">Execute</Button>}
                    {(p.status === "ACTIVE" || p.status === "HARVESTING") && <>
                      <Button size="sm" variant="outline" data-testid={`wildcard-harvest-${p.id.slice(0, 8)}`} onClick={() => act(p.id, "harvest", "ok")} className="h-7 text-[11px] border-[#D4AF37]/50 text-[#D4AF37]">Harvest</Button>
                      <Button size="sm" variant="outline" data-testid={`wildcard-exit-${p.id.slice(0, 8)}`} onClick={() => act(p.id, "exit", "Position closed")} className="h-7 text-[11px] border-red-800 text-red-400">Exit</Button>
                    </>}
                  </div>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>

      <Panel title="Separate Attribution (losses are never hidden in overall reporting)" testId="wildcard-attribution">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 text-sm">
          {[["Capital Contributed to Wildcard", d.capital_contributed], ["Capital Currently Deployed", d.capital_deployed],
            ["Realized Gains", d.realized_gains], ["Realized Losses", d.realized_losses],
            ["Unrealized Gains/Losses", d.unrealized_pl], ["Taxes Reserved", d.estimated_tax_liability],
            ["Capital Available (returned)", d.available_capital], ["Capital Swept to Core", d.gains_swept_to_core]].map(([label, v]) => (
            <div key={label} className="flex justify-between border-b border-[#222630] pb-1.5">
              <span className="text-slate-500 text-xs">{label}</span><Money value={v} className="text-xs" />
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}
